import React, { Component, Suspense } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { Container } from "reactstrap";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { connect } from "react-redux";

import {
  AppBreadcrumb,
  AppFooter,
  AppHeader,
  AppSidebar,
  AppSidebarForm,
  AppSidebarHeader,
  AppSidebarNav,
} from "@coreui/react";

import routes from "../../routes";
import { authorize, logout } from "../../actions/usersActions";
import Loading from "../../components/Loading";
import api from "../../services/api";
import { getComplianceMe } from "../../services/dmsApi";
import {
  getAuthorizedRoutesFromMenu,
  normalizeMenuHierarchy,
} from "../../utils/menuRouteMatcher";
import { ensureWorkingHoursRoutes } from "../../utils/workingHoursRoutes";
import {
  ensureDmsRoutes,
  filterMenuWhenLocked,
  menuHasDmsAdmin,
  menuHasMyDocuments,
} from "../../utils/dmsRoutes";
import {
  ensureBtransSfpRoutes,
  menuHasBtransSfp,
} from "../../utils/btransSfpRoutes";
import {
  ensureFeshedilenFirmalarRoutes,
  menuHasFeshedilenFirmalar,
} from "../../utils/feshedilenFirmalarRoutes";
import {
  ensureDistributorMembersRoutes,
  menuHasDistributorMembers,
} from "../../utils/distributorMembersRoutes";
import {
  ensurePlatePointRoutes,
  menuHasPlatePoint,
} from "../../utils/platePointRoutes";
// Menü etiket/url normalizasyonu (DEV full-menu bypass yok)
import { normalizeDmsMenuItems } from "../../utils/menuDisplay";
import ComplianceBanner from "../Dms/ComplianceBanner";
import { resolveIsDmsAdmin } from "../Dms/utils";
import i18n from "../../translations/index";
import "./style.scss";

const DefaultFooter = React.lazy(() => import("./DefaultFooter"));
const DefaultHeader = React.lazy(() => import("./DefaultHeader"));

const isOutsideWorkingHoursError = (err) =>
  err &&
  (err.message === "OUTSIDE_WORKING_HOURS" ||
    err.name === "OutsideWorkingHours");

const menuHasWorkingHours = (items) => {
  if (!Array.isArray(items)) return false;
  return items.some((item) => {
    const url = ((item && item.url) || "").toLowerCase();
    if (
      url.indexOf("calisma-saatleri") !== -1 ||
      url.indexOf("giris-loglari") !== -1
    ) {
      return true;
    }
    return menuHasWorkingHours(item && item.children);
  });
};

class DefaultLayout extends Component {
  loading = () => (
    <div className="animated fadeIn pt-1 text-center">Yükleniyor...</div>
  );

  constructor(props) {
    super(props);
    this.state = {
      menuItems: {
        items: [
          {
            name: "Anasayfa",
            url: "/home",
            icon: "icon-speedometer",
          },
        ],
      },
      rawMenuItems: [],
      routes: [],
      bootError: null,
      compliance: null,
    };
  }

  componentWillMount() {
    const token = localStorage.getItem("token");
    if (!token) {
      this.props.history.push("/login");
      return;
    }

    this.props
      .authorize()
      .then(() => {})
      .catch((err) => {
        console.error("authorize failed", err);
        if (isOutsideWorkingHoursError(err)) {
          this.props.history.push("/selectProject?outsideHours=1");
          return;
        }
        this.props.history.push("/login");
      });

    let routeList = [];
    const removeList = ["Notifications", "Messages"];

    api
      .getData(`rolematches/menu`)
      .then((res) => {
        const normalizedMenuItems = normalizeMenuHierarchy(res.data);

        for (let route of routes) {
          route.name = this.props.t(route.name);
        }

        const processMenuUrls = (items) => {
          if (!items) return;
          items.forEach((item) => {
            if (item.url && !item.url.startsWith("/")) {
              item.url = "/" + item.url;
            }
            if (item.translate) {
              const translated = this.props.t(item.translate);
              // i18n bazen object döndürür; string değilse dokunma
              if (typeof translated === "string") {
                item.name = translated;
              }
            }
            if (item.children) {
              processMenuUrls(item.children);
            }
          });
        };

        let itemsToProcess = normalizeDmsMenuItems(normalizedMenuItems || []);
        processMenuUrls(itemsToProcess);
        // Çeviriden sonra tekrar etiket/url düzelt + notify gizle
        itemsToProcess = normalizeDmsMenuItems(itemsToProcess);

        // Çalışma Saatleri / DMS menü öğeleri yalnızca backend menüsünde varsa görünür
        // (seed yoksa ekstra enjekte edilmez)

        let items = itemsToProcess.filter(
          (item) => removeList.indexOf(item.name) === -1
        );
        this.setState({
          rawMenuItems: items,
          menuItems: { items },
        });

        routeList = getAuthorizedRoutesFromMenu(res.data, routes);

        // Backend menüsünde WH/DMS varsa route listesini tamamla
        if (menuHasWorkingHours(items)) {
          routeList = ensureWorkingHoursRoutes(routeList, routes);
        }
        if (menuHasDmsAdmin(items)) {
          routeList = ensureDmsRoutes(routeList, routes, { isAdmin: true });
        } else if (menuHasMyDocuments(items)) {
          routeList = ensureDmsRoutes(routeList, routes, { isAdmin: false });
        }
        if (menuHasBtransSfp(items)) {
          routeList = ensureBtransSfpRoutes(routeList, routes);
        }
        if (menuHasFeshedilenFirmalar(items)) {
          routeList = ensureFeshedilenFirmalarRoutes(routeList, routes);
        }
        if (menuHasDistributorMembers(items)) {
          routeList = ensureDistributorMembersRoutes(routeList, routes);
        }
        if (menuHasPlatePoint(items)) {
          routeList = ensurePlatePointRoutes(routeList, routes);
        }

        const homeRoute = routes.find((route) => route.path === "/home");
        if (
          homeRoute &&
          !routeList.some((route) => route.path === homeRoute.path)
        ) {
          routeList = [homeRoute, ...routeList];
        }
      })
      .catch((err) => {
        console.error(err);
        this.setState({
          bootError: (err && err.message) || "Menü yüklenemedi.",
        });
      })
      .finally(() => {
        this.setState({ routes: routeList });
        this.loadCompliance();
      });
  }

  loadCompliance = () => {
    getComplianceMe()
      .then((compliance) => {
        const isAdmin = resolveIsDmsAdmin(compliance);

        this.setState((prev) => {
          let items = normalizeDmsMenuItems([...(prev.rawMenuItems || [])]);

          if (compliance && compliance.isLocked && !isAdmin) {
            items = filterMenuWhenLocked(items);
          }

          return {
            compliance,
            rawMenuItems: items,
            menuItems: { items },
          };
        });

        if (compliance && compliance.isLocked && !isAdmin) {
          const hash = (window.location.hash || "").toLowerCase();
          const allowed =
            hash.indexOf("my-documents") !== -1 ||
            hash.indexOf("/home") !== -1 ||
            hash === "#/" ||
            hash.indexOf("selectproject") !== -1;
          if (!allowed) {
            this.props.history.push("/my-documents?locked=1");
          }
        }
      })
      .catch((err) => {
        console.warn("compliance/me failed", err);
      });
  };

  signOut(e) {
    e.preventDefault();
    this.props.logout();
    localStorage.clear();
    window.location.reload(false);
    localStorage.setItem("language", i18n.language);
    this.props.history.push("/login");
  }

  render() {
    if (this.state.routes.length === 0) {
      if (this.state.bootError) {
        return (
          <div className="p-4 text-center">
            <div className="alert alert-danger">{this.state.bootError}</div>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => this.props.history.push("/selectProject")}
            >
              Proje seçimine dön
            </button>
          </div>
        );
      }
      return (
        <Loading loading background="#f2faff" loaderColor="#3498db" />
      );
    }

    return (
      <div className="app">
        <AppHeader fixed style={{ height: "80px" }}>
          <Suspense fallback={this.loading()}>
            <DefaultHeader
              onLogout={(e) => this.signOut(e)}
              dmsIsAdmin={resolveIsDmsAdmin(this.state.compliance)}
            />
          </Suspense>
        </AppHeader>
        <div style={{ marginTop: "80px" }} className="app-body">
          <AppSidebar style={{ backgroundColor: "#231f20" }} fixed display="lg">
            <AppSidebarHeader />
            <AppSidebarForm />
            <Suspense>
              {this.state.menuItems ? (
                <AppSidebarNav
                  navConfig={this.state.menuItems}
                  {...this.props}
                  colorScheme="red"
                />
              ) : (
                <div className="d-flex justify-content-center mt-4">
                  <Loading color="#FFFFFF" />
                </div>
              )}
            </Suspense>
          </AppSidebar>
          <main className="main">
            <AppBreadcrumb appRoutes={this.state.routes} />
            <Container fluid>
              <ComplianceBanner compliance={this.state.compliance} />
              <Suspense fallback={this.loading()}>
                <Switch>
                  {this.state.routes.map((route, idx) => {
                    return route.component ? (
                      <Route
                        key={idx}
                        path={route.path}
                        exact={route.exact}
                        name={route.name}
                        render={(props) => <route.component {...props} />}
                      />
                    ) : null;
                  })}
                  <Redirect from="/" to="/home" />
                </Switch>
              </Suspense>
            </Container>
          </main>
        </div>
        <AppFooter>
          <Suspense fallback={this.loading()}>
            <DefaultFooter />
          </Suspense>
        </AppFooter>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  loggedIn: state.userReducer.loggedIn,
});

const mapDispatchToProps = {
  authorize,
  logout,
};

export default compose(
  withTranslation("common"),
  connect(mapStateToProps, mapDispatchToProps)
)(DefaultLayout);
